import React, { useState, useEffect } from 'react';
import { clinicDataApi } from '../services/api';

function sumEntries(collections) {
  return collections.reduce((acc, c) => {
    if (c.isFilled && c.entry) {
      acc.leads += c.entry.leads || 0;
      acc.appointments += c.entry.appointments || 0;
      acc.attendances += c.entry.attendances || 0;
      acc.closings += c.entry.closings || 0;
    }
    // Landing page leads are tracked automatically (not part of the manual
    // entry), so they're summed independently of c.isFilled/c.entry.
    if (typeof c.landingPageLeadsCount === 'number') {
      acc.landingPageLeads += c.landingPageLeadsCount;
    }
    return acc;
  }, { leads: 0, appointments: 0, attendances: 0, closings: 0, landingPageLeads: 0 });
}

/**
 * Props:
 *  adAccountIds  – array of UUID strings (DB ids). Omit when passing `data` directly.
 *  startDate     – ISO string
 *  endDate       – ISO string
 *  campaignSpend – optional total spend (number) for CPL calculation
 *  data          – optional pre-fetched result (same shape as the API response).
 *                  When provided, skips the internal fetch — used for shared/
 *                  public reports where the data already came embedded in the
 *                  page payload (no authenticated call available).
 *  readOnly      – hides admin-only affordances (e.g. "Configurar rotinas" link)
 *                  and stays silent when there's nothing filled in yet, since
 *                  public viewers shouldn't be prompted to manage routines.
 */
export default function ClinicDataSection({ adAccountIds, startDate, endDate, campaignSpend, data: dataProp, readOnly = false }) {
  const [data, setData] = useState(dataProp ?? null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (dataProp !== undefined) {
      setData(dataProp || []);
      return;
    }
    if (!adAccountIds || adAccountIds.length === 0) return;
    setLoading(true);
    clinicDataApi.getAccountData(adAccountIds, startDate, endDate)
      .then(res => setData(res.data || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [dataProp, adAccountIds?.join(','), startDate, endDate]);

  const allCollections = data?.flatMap(r => r.collections) || [];
  const totals = sumEntries(allCollections);
  const filledCount = allCollections.filter(c => c.isFilled).length;
  const totalCount = allCollections.length;

  const cpl = campaignSpend && totals.leads > 0 ? (campaignSpend / totals.leads).toFixed(2) : null;
  const appointmentRate = totals.leads > 0 ? ((totals.appointments / totals.leads) * 100).toFixed(1) : null;

  if ((!adAccountIds || adAccountIds.length === 0) && dataProp === undefined) return null;
  if (readOnly && !loading && filledCount === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setOpen(p => !p)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
            <span className="material-symbols-outlined text-teal-600 text-[18px]">local_hospital</span>
          </div>
          <div className="text-left">
            <p className="font-semibold text-slate-800 text-sm">Dados das Clínicas</p>
            {!loading && totalCount > 0 && (
              <p className="text-xs text-slate-400">{filledCount}/{totalCount} coleta{totalCount !== 1 ? 's' : ''} preenchida{totalCount !== 1 ? 's' : ''}</p>
            )}
          </div>
        </div>
        <span className="material-symbols-outlined text-slate-400 transition-transform" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          expand_more
        </span>
      </button>

      {open && (
        <div className="px-6 pb-6 border-t border-slate-100">
          {loading ? (
            <div className="space-y-3 pt-4">
              <div className="h-20 bg-slate-50 rounded-xl animate-pulse" />
              <div className="h-16 bg-slate-50 rounded-xl animate-pulse" />
            </div>
          ) : !data || data.length === 0 ? (
            <div className="text-center py-8">
              <span className="material-symbols-outlined text-slate-300 text-4xl">local_hospital</span>
              <p className="text-sm text-slate-400 mt-2">Nenhuma rotina de coleta configurada para estas contas.</p>
              {!readOnly && (
                <a href="/clinic-data" className="text-xs text-blue-600 hover:underline mt-1 inline-block">Configurar rotinas</a>
              )}
            </div>
          ) : (
            <div className="space-y-5 pt-4">
              {/* KPI Summary */}
              {filledCount > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <KpiCard label="Leads" value={totals.leads} icon="person_add" color="blue" />
                  <KpiCard label="Agend." value={totals.appointments} icon="event" color="purple" />
                  <KpiCard label="Compare." value={totals.attendances} icon="how_to_reg" color="teal" />
                  <KpiCard label="Fechamentos" value={totals.closings} icon="task_alt" color="green" />
                  <KpiCard label="Leads Landing Page" value={totals.landingPageLeads} icon="dns" color="amber" />
                </div>
              )}

              {/* Cross KPIs */}
              {(cpl || appointmentRate) && (
                <div className="grid grid-cols-2 gap-3">
                  {cpl && <CrossKpi label="CPL (Tráfego)" value={`R$ ${cpl}`} sub="Custo por Lead" />}
                  {appointmentRate && <CrossKpi label="% Agend. dos Leads" value={`${appointmentRate}%`} sub="Agendamentos / Leads" />}
                </div>
              )}

              {/* Per routine summary (aggregated — no day-by-day breakdown) */}
              {data.map(routine => routine.collections.length > 0 && (
                <div key={routine.routineId} className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-2.5 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-slate-700">{routine.routineName}</p>
                    <p className="text-[11px] text-slate-400">{routine.adAccountName}</p>
                  </div>
                  <RoutineSummary collections={routine.collections} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, icon, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    teal: 'bg-teal-50 text-teal-600',
    amber: 'bg-amber-50 text-amber-700',
    green: 'bg-green-50 text-green-600',
  };
  return (
    <div className={`rounded-xl p-3 ${colors[color]}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <span className="material-symbols-outlined text-[14px]">{icon}</span>
        <p className="text-[10px] font-medium uppercase tracking-wider opacity-70">{label}</p>
      </div>
      <p className="text-xl font-bold">{value}</p>
    </div>
  );
}

function CrossKpi({ label, value, sub }) {
  return (
    <div className="bg-slate-800 rounded-xl px-4 py-3 text-white">
      <p className="text-[10px] font-medium uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-xl font-bold mt-1">{value}</p>
      <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>
    </div>
  );
}

function RoutineSummary({ collections }) {
  const filled = collections.filter(c => c.isFilled);
  const t = sumEntries(collections); // landingPageLeads sums independently of isFilled
  if (filled.length === 0) return <span className="text-[10px] text-slate-400">Sem dados preenchidos</span>;
  return (
    <div className="flex items-center gap-3 text-[11px] text-slate-600">
      <span>{t.leads} leads</span>
      <span>{t.appointments} agend.</span>
      <span>{t.attendances} compare.</span>
      <span>{t.closings} fech.</span>
      <span>{t.landingPageLeads} leads LP</span>
    </div>
  );
}
